import type {ReactElement} from 'react';
import React from 'react';
import type {FacebookConnectCallback} from '@Components/login-page/login-page.types';
import {Text, TextSize} from '@Components/text';
import {openErrorModal} from '@Modals/error-modal';
import {noop} from '@Utils/general.util';
import {clearModalHistoryEntry} from '@Components/modal-container';
import type {AjaxSuccessResponse} from '@Utils/ajax.util';
import {ButtonSize} from '@Components/button';
import {AuthSsoButton} from '@Components/auth-sso-button/auth-sso-button';
import {useFacebookSignIn} from '@Hooks/login-signup/useFacebookSignIn';
import {getAssetUrl} from '@Utils/s3.util';
import styles from './button-facebook-login.module.scss';

interface ButtonFacebookLoginProps {
  onConnect?: FacebookConnectCallback;
  isSignup?: boolean;
  isConnect?: boolean;
  isDisconnect?: boolean;
  /** Forwarded to the shared SSO shell; the widescreen login page draws these larger. */
  size?: ButtonSize;
  /** The two-column reflow leaves room for the provider name alone, not the full call to action. */
  showProviderNameOnly?: boolean;
}

function ButtonFacebookLogin({showProviderNameOnly = false, onConnect = noop, isSignup = false, isConnect = false, isDisconnect = false, size = ButtonSize.MEDIUM}: ButtonFacebookLoginProps): ReactElement<any> {
  const {signInWithFacebook, connectFacebookAccount} = useFacebookSignIn({onConnect});

  const getFacebookLogo = (): ReactElement => {
    return <img alt="" className={styles.brandMark} src={getAssetUrl('facebook-logo-thumb-circle.png')} />;
  };

  const getButtonText = (): string => {
    if (showProviderNameOnly) {
      return window.i18next.t('pmwjs_facebook');
    }

    return isSignup ? window.i18next.t('pmwjs_sign_in_with_facebook') : window.i18next.t('pmwjs_log_in_with_facebook');
  };

  const disconnectFacebookAccount = (): void => {
    clearModalHistoryEntry();
    void window.PMW.write(window.PMW.util.site_url('user/disconnectFacebookAccount'), null, (response: AjaxSuccessResponse) => {
      if (response.status === 'success') {
        window.location.href = window.PMW.util.site_url('user/editprofile');
      } else {
        openErrorModal({message: window.i18next.t('pmwjs_error_processing_request_try_again')});
      }
    });
  };

  const onDisconnectFacebookAccount = async (): Promise<void> => {
    const response = await window.PMW.readLocal('authenticate/isEmailAccountUser');
    if (!('isEmailAccountUser' in response) || !('is2FAEnabled' in response)) {
      openErrorModal({message: window.i18next.t('pmwjs_error_processing_request_try_again')});
      return;
    }

    if (response.isEmailAccountUser) {
      window.PMW.openPasswordVerificationModal({
        onSuccessCallback: onDisconnectPasswordVerification,
        check2FAStatus: true
      });
    } else if (response.is2FAEnabled) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: disconnectFacebookAccount,
        onlyTOTPValid: true
      });
    } else {
      disconnectFacebookAccount();
    }
  }

  const onDisconnectPasswordVerification = (is2FAPending = false): void => {
    if (is2FAPending) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: disconnectFacebookAccount,
        onlyTOTPValid: true
      });
    } else {
      disconnectFacebookAccount();
    }
  }

  const onConnectFacebookAccount = async (): Promise<void> => {
    const response = await window.PMW.readLocal('authenticate/isEmailAccountUser');
    if (!('isEmailAccountUser' in response) || !('is2FAEnabled' in response)) {
      openErrorModal({message: window.i18next.t('pmwjs_error_processing_request_try_again')});
      return;
    }

    if (response.isEmailAccountUser) {
      window.PMW.openPasswordVerificationModal({
        onSuccessCallback: onConnectPasswordVerification,
        check2FAStatus: true
      });
    } else if (response.is2FAEnabled) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: connectFacebookAccount,
        onlyTOTPValid: true
      });
    } else {
      void connectFacebookAccount();
    }
  }

  const onConnectPasswordVerification = (is2FAPending = false): void => {
    if (is2FAPending) {
      window.PMW.openTwoFactorAuthenticationVerificationModal({
        onVerificationSuccessCallback: connectFacebookAccount,
        onlyTOTPValid: true
      });
    } else {
      void connectFacebookAccount();
    }
  }

  if (isDisconnect) {
    return <Text val={window.i18next.t('pmwjs_disconnect')} size={TextSize.MD} onClick={(): void => {
      void onDisconnectFacebookAccount();
    }} />;
  }

  if (isConnect) {
    return (
      <Text
        val={window.i18next.t('pmwjs_connect')}
        size={TextSize.MD}
        onClick={(): void => {
          void onConnectFacebookAccount();
        }}
      />
    );
  }

  return <AuthSsoButton customIcon={getFacebookLogo()} onClick={signInWithFacebook} size={size} text={getButtonText()} />;
}

export default React.memo(ButtonFacebookLogin);
